import React, { useRef, useEffect, useState } from "react"
import CustomLink from "./CustomLink"
import { setNonDispalyValue } from "./EventHandler"
import { Button, Modal, ModalDialog } from "react-bootstrap"
import Draggable from "react-draggable"
import { LegendData } from "../../../constant/Constants"

const CustomPCTable = (props) => {
   const [changeOccure, setChangeOccure] = useState(false)
   const [currData, setData] = useState()
   const [previousData, setPreviousData] = useState()
   const [partName] = useState(props.partName)

   const [show, setShow] = useState(false)
   const handleClose = () => setShow(false)
   const handleShow = () => setShow(true)

   const splitLegendData = (data) => {
      const entries = Object.entries(data)
      const firstHalf = entries.slice(0, 14)
      const secondHalf = entries.slice(14)

      return {
         firstHalf: Object.fromEntries(firstHalf),
         secondHalf: Object.fromEntries(secondHalf),
      }
   }

   const { firstHalf, secondHalf } = splitLegendData(LegendData)

   const getTableRows = (data) => {
      return Object.entries(data).map(([key, value], index) => (
         <tr key={index}>
            <td>{key}</td>
            <td>{value}</td>
         </tr>
      ))
   }

   useEffect(() => {
      if (props.data) {
         setData(props.data.sort())
      }
      if (props.previousData) {
         setPreviousData(props.previousData.sort())
      }
   }, [props.data, props.previousData])

   const containerRef = useRef(null)
   const table1Ref = useRef(null)
   const table2Ref = useRef(null)

   useEffect(() => {
      if (changeOccure) {
         const delay = setTimeout(() => {
            props.handleDiffCategory(props.name)
         }, 1000) // Add a 1-second delay

         // Cleanup the timeout to avoid memory leaks
         return () => clearTimeout(delay)
      }
   }, [changeOccure, props.name, props.handleDiffCategory])

   useEffect(() => {
      const synchronizeRowHeights = () => {
         const table1Rows = table1Ref.current?.querySelectorAll("tr") || []
         const table2Rows = table2Ref.current?.querySelectorAll("tr") || []

         // Ensure both tables have the same number of rows
         const rowCount = Math.min(table1Rows.length, table2Rows.length)

         for (let i = 0; i < rowCount; i++) {
            const table1Row = table1Rows[i]
            const table2Row = table2Rows[i]

            // Get the maximum height of the two rows
            const maxHeight = Math.max(table1Row.offsetHeight, table2Row.offsetHeight)

            // Apply the maximum height to both rows
            table1Row.style.height = `${maxHeight}px`
            table2Row.style.height = `${maxHeight}px`
         }
      }

      // Synchronize row heights initially
      synchronizeRowHeights()

      // Synchronize row heights on window resize
      window.addEventListener("resize", synchronizeRowHeights)

      return () => {
         window.removeEventListener("resize", synchronizeRowHeights)
      }
   }, [currData, previousData])

   useEffect(() => {
      let isSyncing = false

      const handleScrollTable1 = () => {
         if (isSyncing) return
         isSyncing = true
         if (table2Ref.current) {
            table2Ref.current.scrollTop = table1Ref.current.scrollTop || 0
            table2Ref.current.scrollLeft = table1Ref.current.scrollLeft || 0
         }
         isSyncing = false
      }

      const handleScrollTable2 = () => {
         if (isSyncing) return
         isSyncing = true
         if (table1Ref.current) {
            table1Ref.current.scrollTop = table2Ref.current.scrollTop || 0
            table1Ref.current.scrollLeft = table2Ref.current.scrollLeft || 0
         }
         isSyncing = false
      }

      const table1 = table1Ref.current
      const table2 = table2Ref.current

      if (table1) {
         table1.addEventListener("scroll", handleScrollTable1)
      }

      if (table2) {
         table2.addEventListener("scroll", handleScrollTable2)
      }

      return () => {
         if (table1) {
            table1.removeEventListener("scroll", handleScrollTable1)
         }
         if (table2) {
            table2.removeEventListener("scroll", handleScrollTable2)
         }
      }
   }, [currData, previousData])
   const getHeader = () => {
      var keys = getKeys()
      return keys.map((key, index) => {
         if (setNonDispalyValue(key)) {
            return null
         }
         return (
            <th key={key}>
               {key}
            </th>
         )
      })
   }

   const compareObjects = (current, previous, parentKey = "") => {
      let differences = {}

      if (
         typeof current === "object" &&
         current !== null &&
         typeof previous === "object" &&
         previous !== null
      ) {
         // If both are objects, compare their keys recursively
         const keys = new Set([...Object.keys(current), ...Object.keys(previous)])
         keys.forEach((key) => {
            const newKey = parentKey ? `${parentKey}.${key}` : key // Flatten the key
            Object.assign(
               differences,
               compareObjects(current[key], previous[key], newKey)
            )
         })
      } else {
         // For primitive values, compare directly
         const flattenedKey = parentKey
         differences[flattenedKey] = current === previous ? "same" : "different"
      }

      return differences
   }

   const isObjectEmpty = (obj) => {
      return Object.values(obj).every((value) => {
         if (typeof value === "object" && value !== null) {
            // Recursively check nested objects
            return isObjectEmpty(value)
         }
         return !value // Check if the value is empty or falsy
      })
   }

   const differencesInEachRow = (index) => {
      let differencesInRow = {}
      // Ensure both rows exist
      if (currData[index] && previousData[index]) {
         if (!isObjectEmpty(currData[index]) && !isObjectEmpty(previousData[index])) {
            differencesInRow = compareObjects(currData[index], previousData[index])
         }
      }

      return differencesInRow
   }

   const areBothTablesEmpty = () => {
      const isCurrDataEmpty =
         !currData ||
         currData.length === 0 ||
         currData.every((row) => {
            return Object.values(row).every((value) => {
               if (typeof value === "object" && value !== null) {
                  return Object.values(value).every((subValue) => !subValue)
               }
               return !value
            })
         })

      const isPreviousDataEmpty =
         !previousData ||
         previousData.length === 0 ||
         previousData.every((row) => {
            return Object.values(row).every((value) => {
               if (typeof value === "object" && value !== null) {
                  return Object.values(value).every((subValue) => !subValue)
               }
               return !value
            })
         })

      return isCurrDataEmpty && isPreviousDataEmpty
   }

   const getRowsData = (data) => {
      var items = data
      var keys = getKeys()
      let backgroundColor = ""

      return items.map((row, index) => {
         // Check if the row or its sub-objects are empty
         const isRowEmpty =
            !row ||
            Object.values(row).every((value) => {
               if (typeof value === "object" && value !== null) {
                  // Check if sub-object is empty
                  return Object.values(value).every((subValue) => !subValue)
               }
               return !value // Check if the value itself is empty
            })

         if (isRowEmpty && !areBothTablesEmpty()) {
            // Render an empty row
            if (changeOccure == false) {
               setChangeOccure(true)
               props.handleDiffCategory(props.name)
            }
            return (
               <tr key={index}>
                  <td colSpan={keys.length} style={{ backgroundColor: "#ffcccc" }}></td>
               </tr>
            )
         }
         if (areBothTablesEmpty()) {
            return (
               <tr key={index}>
                  <td colSpan={keys.length} style={{ backgroundColor: "white" }}></td>
               </tr>
            )
         }
         let differencesInRow = differencesInEachRow(index)

         if (Object.values(differencesInRow).includes("different")) {
            if (changeOccure == false) {
               setChangeOccure(true)
               props.handleDiffCategory(props.name)
            }
            backgroundColor = "#ffcccc"
         } else {
            backgroundColor = "white"
         }
         return (
            <tr key={index} style={{ backgroundColor: backgroundColor }}>
               <RenderRow
                  key={index}
                  data={row}
                  keys={keys}
                  differencesInRow={differencesInRow}
               />
            </tr>
         )
      })
   }

   const getKeys = function () {
      return Object.keys(currData[0])
   }

   const getColumnKeys = function (columKeyInfo) {
      return Object.keys(columKeyInfo)
   }

   const getPCLinks = function (data, link, id, color) {
      var elements = data.map((value, index) => {
         var num = index + 1
         if (
            link[index] &&
            link[index].trim() !== "" &&
            id[index] &&
            id[index].trim() !== "" &&
            id[index].toLowerCase().trim() !== "no access"
         ) {
            return (
               <span key={index}>
                  <CustomLink
                     id={id[index].trim()}
                     link={link[index].trim()}
                     name={value}
                     color={color}
                  />
                  {num === data.length ? "" : "|"}
               </span>
            )
         } else {
            return (
               <span key={index}>
                  {value}
                  {num === data.length ? "" : "|"}
               </span>
            )
         }
      })
      return elements
   }

   const RenderColumn = function (props) {
      return props.keys.map((key, index) => {
         if (props.data[key] != null) {
            if (setNonDispalyValue(key)) {
               return null
            } else if (
               key.toLowerCase().trim() === "p" &&
               props.data["id"] &&
               props.data["id"].toLowerCase().trim() !== "no access"
            ) {
               let name = props.data[key]
               let color = ""

               if (props.childKeysWithStatus) {
                  if (props.childKeysWithStatus["P"] == "different") {
                     color = "red"
                  }
               }

               return (
                  <div key={index} style={{ minWidth: "9rem" }}>
                     <span className={props.isbold ? "bomFontClass" : ""}>{key}:</span>{" "}
                     <span>
                        {props.data["link"] ? (
                           <CustomLink
                              id={props.data["id"]}
                              link={props.data["link"]}
                              name={name}
                              color={color}
                           />
                        ) : (
                           <span style={{ color: color, fontWeight: color?'bold':"" }}>{name}</span>
                        )}
                     </span>
                  </div>
               )
            } else if (key.toLowerCase() === "name") {
               let nameData = props.data[key] ? props.data[key].split("|") : []
               let nameLink = props.data["methodLink"]
                  ? props.data["methodLink"].split("|")
                  : []
               let nameId = props.data["methodId"]
                  ? props.data["methodId"].split("|")
                  : []
               let nameColor = ""
               if (props.childKeysWithStatus) {
                  if (props.childKeysWithStatus["NAME"] == "different") {
                     nameColor = "red"
                  }
               }
               let getnamelinks = getPCLinks(nameData, nameLink, nameId, nameColor)
               return (
                  <div key={index}>
                     <span className={props.isbold ? "bomFontClass" : ""}>{key}: </span>
                     {getnamelinks}
                  </div>
               )
            } else if (
               typeof props.data[key] == "object" &&
               key &&
               key.toLowerCase() === "cps"
            ) {
               let names = props.data[key]["name"]
                  ? props.data[key]["name"].split("|")
                  : []
               let links = props.data[key]["link"]
                  ? props.data[key]["link"].split("|")
                  : []
               let ids = props.data[key]["id"] ? props.data[key]["id"].split("|") : []
               let getLinks = getPCLinks(names, links, ids)
               return (
                  <div key={index}>
                     <span className={props.isbold ? "bomFontClass" : ""}>{key}: </span>
                     {getLinks}
                  </div>
               )
            } else if (
               key.toLowerCase() === "ref" ||
               key.toLowerCase().includes("tmrdn")
            ) {
               var refData = props.data[key] ? props.data[key].split("|") : []
               var refLink = props.data["refLink"] ? props.data["refLink"].split("|") : []
               var refId = props.data["refId"] ? props.data["refId"].split("|") : []
               let tmrdnColor = ""
               if (props.childKeysWithStatus) {
                  if (props.childKeysWithStatus["(TMRDN)"] == "different") {
                     tmrdnColor = "red"
                  }
               }
               var getlinks = getPCLinks(refData, refLink, refId, tmrdnColor)
               return (
                  <div key={index}>
                     <span className={props.isbold ? "bomFontClass" : ""}>{key}: </span>
                     {getlinks}
                  </div>
               )
            } else if (props.data[key] != null && key.toLowerCase() === "cmn") {
               var name = props.data[key]["name"]
                  ? props.data[key]["name"].split("|")
                  : []
               var link = props.data[key]["link"]
                  ? props.data[key]["link"].split("|")
                  : []
               var id = props.data[key]["id"] ? props.data[key]["id"].split("|") : []
               var getlinks = getPCLinks(name, link, id)
               return (
                  <div key={index}>
                     <span className={props.isbold ? "bomFontClass" : ""}>{key}: </span>
                     {getlinks}
                  </div>
               )
            }
            if (props.childKeysWithStatus) {
               if (props.childKeysWithStatus[key] == "different") {
                  return (
                     <div key={index}>
                        <span className={props.isbold ? "bomFontClass" : ""}>{key}:</span>{" "}
                        <span style={{ color: "red", fontWeight: "bold" }}>
                           {props.data[key]}
                        </span>
                     </div>
                  )
               }
            }

            return (
               <div key={index}>
                  <span className={props.isbold ? "bomFontClass" : ""}>{key}:</span>{" "}
                  <span className="lineBreak">{props.data[key]}</span>
               </div>
            )
         } else return null
      })
   }

   const RenderRow = (props) => {
      let color = ""

      if (props.differencesInRow) {
         if (props.differencesInRow["P"]) {
            color = "red"
         }
      }
      var colKeys = []
      return props.keys.map((key, index) => {
         if (props.data[key] != null && typeof props.data[key] == "object") {
            colKeys = getColumnKeys(props.data[key])

            const childKeysWithStatus = Object.keys(props.differencesInRow || {})
               .filter((diffKey) => diffKey.startsWith(`${key}.`)) // Match keys that start with the parent key
               .reduce((acc, diffKey) => {
                  const childKey = diffKey.split(".").pop() // Extract the child key
                  acc[childKey] = props.differencesInRow[diffKey] // Map child key to its status
                  return acc
               }, {})

            return (
               <td key={index}>
                  <RenderColumn
                     isbold={true}
                     data={props.data[key]}
                     keys={colKeys}
                     color={color}
                     childKeysWithStatus={childKeysWithStatus}
                  />
               </td>
            )
         } else if (setNonDispalyValue(key)) {
            return null
         } else {
            if (props.differencesInRow) {
               if (props.differencesInRow["Chg."] == "different") {
                  return (
                     <td key={index} style={{ color: "red", fontWeight: "bold" }}>
                        {props.data[key]}
                     </td>
                  )
               }
            }
            return <td key={index}>{props.data[key]}</td>
         }
      })
   }

   return (
      <div>
         <div className="actionDetails1" onClick={handleShow}>
            PC Legend
         </div>

         <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            style={{ fontFamily: "var(--app-font-family)" }}
            dialogAs={(props) => (
               <Draggable handle=".legendTableTitle">
                  <ModalDialog {...props} />
               </Draggable>
            )}
         >
            <Modal.Header className="legendTableTitle">
               <Modal.Title>Legend:</Modal.Title>
            </Modal.Header>
            <Modal.Body className="legendTableBodyStyle">
               <div style={{ display: "flex", gap: "20px" }}>
                  <table className="legendTable">
                     <tbody>{getTableRows(firstHalf)}</tbody>
                  </table>
                  {Object.keys(secondHalf).length > 0 && (
                     <table className="legendTable">
                        <tbody>{getTableRows(secondHalf)}</tbody>
                     </table>
                  )}
               </div>
            </Modal.Body>
            <Modal.Footer>
               <Button
                  style={{ backgroundColor: "#003DAF" }}
                  variant="primary"
                  onClick={handleClose}
               >
                  Close
               </Button>
            </Modal.Footer>
         </Modal>
         <div ref={containerRef} style={{ display: "flex" }}>
            <div
               ref={table1Ref}
               style={{ width: "50%", overflow: "auto", maxHeight: "60vh" }}
            >
               {props.revision && props.revision !== "" && (
                  <div
                     style={{
                        position: "sticky",
                        top: "0",
                        backgroundColor: "#EEEEEE",
                        textAlign: "center",
                        fontWeight: "bold",
                     }}
                  >
                     {partName} {props.revision["releaseRevision"]}
                  </div>
               )}
               {currData && previousData && currData.length > 0 && (
                  <table className="tableBorder">
                     <thead
                        style={{
                           visibility:
                              props.isHeader !== "false" ? "visible" : "collapse",
                        }}
                     >
                        <tr style={{ backgroundColor: "#eee" }}>{getHeader()}</tr>
                     </thead>
                     <tbody>{getRowsData(currData)}</tbody>
                  </table>
               )}
            </div>

            <div
               ref={table2Ref}
               style={{ width: "50%", overflow: "auto", maxHeight: "60vh" }}
            >
               {props.revision && props.revision !== "" && (
                  <div
                     style={{
                        position: "sticky",
                        top: "0",
                        backgroundColor: "#EEEEEE",
                        textAlign: "center",
                        fontWeight: "bold",
                     }}
                  >
                     {partName} {props.revision["obsoleteRevision"]}
                  </div>
               )}
               {currData && previousData && previousData.length > 0 && (
                  <table className="tableBorder">
                     <thead
                        style={{
                           visibility:
                              props.isHeader !== "false" ? "visible" : "collapse",
                        }}
                     >
                        <tr style={{ backgroundColor: "#eee" }}>{getHeader()}</tr>
                     </thead>
                     <tbody>{getRowsData(previousData)}</tbody>
                  </table>
               )}
            </div>
         </div>
      </div>
   )
}
export default CustomPCTable
